import React, { useCallback, useEffect, useMemo } from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import {
	useActiveTabSelector,
	useUIStoreActions,
} from '@atlassian/conversation-assistant-store/controllers/ui-store';

import {
	FOR_YOU_TAB_ID,
	type RovoTabId,
	SUGGESTIONS_TAB_ID,
} from '../chat-history/rovo-tabs/constants';

import { ActiveTabContext } from './ActiveTabContext';

function readMiniModalShellHeightPx(
	shellRef: React.RefObject<HTMLDivElement | null> | undefined,
): number | null {
	if (!shellRef?.current) {
		return null;
	}
	const h = Math.floor(shellRef.current.getBoundingClientRect().height);
	return h > 0 ? h : null;
}

/** Active tab for mini-modal RovoTabs; optionally snapshots shell height before tab changes. */
export const ActiveTabProvider = ({
	currentConversationId,
	children,
	miniModalShellRef,
	captureShellHeightOnTabChange = false,
	onShellHeightCapture,
	selectedAgentId,
}: {
	currentConversationId: string;
	children: React.ReactNode;
	miniModalShellRef?: React.RefObject<HTMLDivElement | null>;
	captureShellHeightOnTabChange?: boolean;
	/** Called with the tab ID being left and the captured height before the switch.
	 *  The parent routes each height to the appropriate setter (Suggestions vs For You). */
	onShellHeightCapture?: (fromTabId: string, heightPx: number) => void;
	/**
	 * ID of the currently selected non-default agent. When set, resets the active
	 * tab to Suggestions so the agent greeting is shown without the tab bar.
	 * Saves the previous tab so it can be restored on back navigation.
	 */
	selectedAgentId?: string;
}): React.JSX.Element => {
	const [{ activeTabId: activeTabIdRaw, previousTabId: previousTabIdRaw, lastTabConversationId }] =
		useActiveTabSelector();
	const { setActiveTabId, setPreviousTabId, setLastTabConversationId } = useUIStoreActions();
	const activeTabId = activeTabIdRaw as RovoTabId;
	const previousTabId = previousTabIdRaw as RovoTabId;

	// Reads and reports the shell height. The boolean guard is checked at the
	// call site so this callback's identity stays stable across prop changes.
	const captureShellHeight = useCallback(
		(fromTabId: string) => {
			if (!onShellHeightCapture) {
				return;
			}
			const h = readMiniModalShellHeightPx(miniModalShellRef);
			if (h !== null) {
				onShellHeightCapture(fromTabId, h);
			}
		},
		[onShellHeightCapture, miniModalShellRef],
	);

	const handleActiveTabChange = useCallback(
		(tabId: string) => {
			if (tabId !== FOR_YOU_TAB_ID && tabId !== SUGGESTIONS_TAB_ID) {
				return;
			}
			if (captureShellHeightOnTabChange) {
				// Capture the height of the tab we're leaving before switching
				captureShellHeight(activeTabId);
			}
			// Save the tab being left so `resetToPreviousTab()` can restore it later.
			// Prior to this, `previousTabId` was only written by the auto-switch effects
			// (conversation-created / agent-selected) — meaning a user manually clicking
			// between tabs left `previousTabId` stale. That broke back-navigation from
			// space launches that originated via manual tab clicks (For You → space
			// landing → back would land on Suggestions because `previousTabId` still
			// held its default 'suggestions'). Writing it on every user-initiated tab
			// change keeps the invariant "the tab I was on before the current one" true.
			if (tabId !== activeTabId) {
				setPreviousTabId(activeTabId);
			}
			setActiveTabId(tabId);
		},
		[
			captureShellHeight,
			captureShellHeightOnTabChange,
			setActiveTabId,
			setPreviousTabId,
			activeTabId,
		],
	);

	const resetToPreviousTab = useCallback(() => {
		if (captureShellHeightOnTabChange) {
			captureShellHeight(activeTabId);
		}
		setActiveTabId(previousTabId);
	}, [
		captureShellHeight,
		captureShellHeightOnTabChange,
		setActiveTabId,
		previousTabId,
		activeTabId,
	]);

	// When a conversation becomes active (e.g. via Daily Brief CTA),
	// save the current tab so we can restore it on back navigation,
	// then switch to Suggestions so the mini-modal chat input is available.
	useEffect(() => {
		if (currentConversationId === lastTabConversationId) {
			return;
		}
		setLastTabConversationId(currentConversationId);
		if (currentConversationId !== EMPTY_CONVERSATION_ID) {
			setPreviousTabId(activeTabId);
			setActiveTabId(SUGGESTIONS_TAB_ID);
		}
	}, [currentConversationId]); // eslint-disable-line react-hooks/exhaustive-deps

	// When the user selects a non-default agent while on the For You tab,
	// switch back to Suggestions so the agent greeting is visible.
	// Save the current tab so it can be restored when the agent is deselected.
	useEffect(() => {
		if (selectedAgentId) {
			setPreviousTabId(activeTabId);
			setActiveTabId(SUGGESTIONS_TAB_ID);
		}
	}, [selectedAgentId]); // eslint-disable-line react-hooks/exhaustive-deps

	const memoizedValue = useMemo(
		() => ({ activeTabId, handleActiveTabChange, resetToPreviousTab }),
		[activeTabId, handleActiveTabChange, resetToPreviousTab],
	);

	const value =
		FeatureGates.getExperimentValue<boolean>(
			'rovo_ttai_segment_render_stabilization',
			'isEnabled',
			false,
		) === true
			? memoizedValue
			: { activeTabId, handleActiveTabChange, resetToPreviousTab };

	return <ActiveTabContext.Provider value={value}>{children}</ActiveTabContext.Provider>;
};
